import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';
import { Button } from '@/components/ui/button';
import NotificationBell from '@/components/NotificationBell';
import CloudyAIChatbot from '@/components/CloudyAIChatbot';
import IdleTimeoutWarning from '@/components/IdleTimeoutWarning';
import {
  Menu,
  X,
  Home,
  Users,
  User,
  GraduationCap,
  BookOpen,
  Calendar,
  DollarSign,
  Library,
  Bell,
  ClipboardList,
  Bus,
  Settings,
  LogOut,
  Moon,
  Sun,
  Building2,
  Wallet,
  UserCog,
  FileText,
  BellRing,
  Clock,
  CalendarRange,
  MessageCircle,
  BarChart3,
  CreditCard,
  Ticket,
  Mail,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Receipt,
  Send,
  Building,
  BookOpenCheck,
  Award,
  Key,
  TrendingUp,
  ShoppingCart,
  UserCheck,
  DoorOpen,
  QrCode,
  Shield,
  MapPin,
  Fingerprint,
  Smartphone,
  Package
} from 'lucide-react';

const DashboardLayout = ({ children, title }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, subscriptionStatus, isReadOnly, userPermissions, hasModuleAccess, hasTransportAllocation, hasHostelAllocation } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Idle timeout hook for auto-logout
  const { showWarning, remainingTime, logout: idleLogout, stayLoggedIn } = useIdleTimeout();

  const handleLogout = () => {
    // Logout returns login_source for redirect
    const loginSource = logout();
    
    // Redirect to where user originally logged in from
    window.location.href = loginSource;
  };

  // Module mapping: maps menu item paths to module IDs
  const moduleMapping = {
    '/students': 'students',
    '/teachers': 'teachers',
    '/staff': 'staff',
    '/classes': 'students', // Classes is part of student module
    '/timetable': 'timetable',
    '/attendance': 'attendance',
    '/exams': 'exams',
    '/online-exam': 'exams',
    '/fee-management': 'fees',
    '/accounts': 'accounts',
    '/accounting': 'accounts',
    '/staff-qr-attendance': 'staff',
    '/leave-config': 'staff',
    '/salary': 'staff',
    '/reports': 'reports',
    '/analytics': 'reports',
    '/academic': 'settings',
    '/academic-calendar': 'timetable',
    '/promotion': 'students',
    '/store': 'store',
    '/behavior': 'behavior',
    '/announcements': 'notifications',
    '/communication': 'notifications',
    '/fee-reminders': 'fees',
    '/library': 'library',
    '/notices': 'notifications',
    '/homework': 'homework',
    '/certificates': 'certificates',
    '/id-cards': 'students',
    '/id-card-builder': 'students',
    '/alumni': 'students',
    '/hostel': 'hostel',
    '/gate-pass': 'hostel',
    '/transport': 'transport',
    '/integrations': 'settings',
    '/password-reset': 'settings',
    '/user-credentials': 'settings',
    '/staff-roles': 'settings',
    '/my-subscription': 'settings',
  };

  // Check if a staff user has access to a menu item
  const staffHasAccess = (path) => {
    const moduleId = moduleMapping[path];
    if (!moduleId) return true; // Allow if no mapping (dashboard, etc.)
    return hasModuleAccess(moduleId);
  };

  const getNavigationItems = () => {
    const baseItems = [
      { name: 'Dashboard', icon: Home, path: getDashboardPath(), roles: ['super_admin', 'school_admin', 'teacher', 'student', 'parent', 'staff'] },
      
      // ═══════════════════════════════════════════════════════════════
      // MY PROFILE - For all users (placed at top for easy access)
      // ═══════════════════════════════════════════════════════════════
      { name: 'My Profile', icon: UserCog, path: '/admin/profile', roles: ['school_admin', 'super_admin'] },
      { name: 'My Profile', icon: UserCog, path: '/staff/profile', roles: ['teacher', 'staff'] },
      { name: 'My Profile', icon: User, path: '/student/profile', roles: ['student'] },
      { name: 'My Profile', icon: User, path: '/parent/profile', roles: ['parent'] },
      
      // ═══════════════════════════════════════════════════════════════
      // SUPER ADMIN ITEMS
      // ═══════════════════════════════════════════════════════════════
      { name: 'Subscriptions', icon: CreditCard, path: '/super-admin/subscriptions', roles: ['super_admin'] },
      { name: 'Billing & Invoices', icon: Receipt, path: '/super-admin/invoices', roles: ['super_admin'] },
      { name: 'Broadcast', icon: Send, path: '/super-admin/broadcast', roles: ['super_admin'] },
      { name: 'Support Tickets', icon: Ticket, path: '/super-admin/tickets', roles: ['super_admin'] },
      { name: 'Settings', icon: Settings, path: '/super-admin/providers', roles: ['super_admin'] },
      
      // ═══════════════════════════════════════════════════════════════
      // SCHOOL ADMIN - CORE MANAGEMENT
      // ═══════════════════════════════════════════════════════════════
      { name: 'Students', icon: Users, path: '/students', roles: ['school_admin', 'staff'] },
      { name: 'My Students', icon: Users, path: '/students', roles: ['teacher'] },
      { name: 'Teachers', icon: GraduationCap, path: '/teachers', roles: ['school_admin'] },
      { name: 'Staff', icon: UserCog, path: '/staff', roles: ['school_admin'] },
      { name: 'Classes', icon: BookOpen, path: '/classes', roles: ['school_admin'] },
      
      // ═══════════════════════════════════════════════════════════════
      // ACADEMICS
      // ═══════════════════════════════════════════════════════════════
      { name: 'Academic Year', icon: CalendarRange, path: '/academic', roles: ['school_admin'] },
      { name: 'Academic Calendar', icon: Calendar, path: '/academic-calendar', roles: ['school_admin'] },
      { name: 'View Calendar', icon: Calendar, path: '/academic-calendar', roles: ['teacher', 'staff', 'student', 'parent'] },
      { name: 'Timetable', icon: Clock, path: '/timetable', roles: ['school_admin'] },
      { name: 'My Timetable', icon: Clock, path: '/timetable', roles: ['teacher', 'staff', 'student'] },
      { name: 'Student Promotion', icon: TrendingUp, path: '/promotion', roles: ['school_admin'] },
      
      // ═══════════════════════════════════════════════════════════════
      // ATTENDANCE
      // ═══════════════════════════════════════════════════════════════
      { name: 'Attendance', icon: Calendar, path: '/attendance', roles: ['school_admin', 'teacher', 'staff'] },
      { name: 'My Attendance', icon: Calendar, path: '/attendance', roles: ['student'] },
      { name: 'Child Attendance', icon: Calendar, path: '/parent/attendance', roles: ['parent'] },
      { name: 'Biometric', icon: Fingerprint, path: '/biometric', roles: ['school_admin'] },
      { name: 'Staff QR Attendance', icon: QrCode, path: '/staff-qr-attendance', roles: ['school_admin'] },
      
      // ═══════════════════════════════════════════════════════════════
      // EXAMS & RESULTS
      // ═══════════════════════════════════════════════════════════════
      { name: 'Exams', icon: ClipboardList, path: '/exams', roles: ['school_admin', 'teacher', 'staff'] },
      { name: 'My Exams', icon: ClipboardList, path: '/exams', roles: ['student'] },
      { name: 'Child Results', icon: ClipboardList, path: '/parent/results', roles: ['parent'] },
      { name: 'Online Exam', icon: ClipboardList, path: '/online-exam', roles: ['school_admin', 'teacher', 'student', 'staff'] },
      
      // ═══════════════════════════════════════════════════════════════
      // HOMEWORK & LIBRARY
      // ═══════════════════════════════════════════════════════════════
      { name: 'Homework & Assignments', icon: BookOpenCheck, path: '/homework', roles: ['school_admin', 'teacher', 'staff'] },
      { name: 'My Homework', icon: BookOpenCheck, path: '/homework', roles: ['student'] },
      { name: 'Library', icon: Library, path: '/library', roles: ['school_admin', 'staff'] },
      { name: 'My Library', icon: Library, path: '/library', roles: ['student'] },
      
      // ═══════════════════════════════════════════════════════════════
      // FEES & ACCOUNTS
      // ═══════════════════════════════════════════════════════════════
      { name: 'Fee Management', icon: Wallet, path: '/fee-management', roles: ['school_admin', 'staff'] },
      { name: 'My Fees', icon: Wallet, path: '/fee-management', roles: ['student', 'parent'] },
      { name: 'Fee Reminders', icon: BellRing, path: '/fee-reminders', roles: ['school_admin', 'staff'] },
      { name: 'Accounts', icon: Wallet, path: '/accounts', roles: ['school_admin', 'staff'] },
      { name: 'Accounting', icon: Receipt, path: '/accounting', roles: ['school_admin'] },
      { name: 'Suppliers', icon: Building2, path: '/suppliers', roles: ['school_admin', 'staff'] },
      { name: 'Inventory', icon: Package, path: '/inventory', roles: ['school_admin', 'staff'] },
      { name: 'School Store', icon: ShoppingCart, path: '/store', roles: ['school_admin'] },
      
      // ═══════════════════════════════════════════════════════════════
      // HR & PAYROLL
      // ═══════════════════════════════════════════════════════════════
      { name: 'Salary Slips', icon: DollarSign, path: '/salary', roles: ['school_admin'] },
      { name: 'Leave Configuration', icon: CalendarRange, path: '/leave-config', roles: ['school_admin'] },
      { name: 'Staff Roles', icon: Shield, path: '/staff-roles', roles: ['school_admin'] },
      { name: 'My Attendance', icon: Calendar, path: '/staff/my-attendance', roles: ['teacher', 'staff'] },
      { name: 'My Salary', icon: DollarSign, path: '/staff/my-salary', roles: ['teacher', 'staff'] },
      { name: 'Apply Leave', icon: CalendarRange, path: '/staff/leave', roles: ['teacher', 'staff'] },
      
      // ═══════════════════════════════════════════════════════════════
      // HOSTEL & GATE PASS
      // ═══════════════════════════════════════════════════════════════
      { name: 'Hostel', icon: Building, path: '/hostel', roles: ['school_admin'] },
      { name: 'My Hostel', icon: Building, path: '/hostel', roles: ['student'], requiresHostel: true },
      { name: 'Gate Pass', icon: DoorOpen, path: '/gate-pass', roles: ['school_admin', 'staff', 'teacher'] },
      { name: 'My Gate Pass', icon: DoorOpen, path: '/gate-pass', roles: ['student'], requiresHostel: true },
      { name: 'Gate Pass Approvals', icon: DoorOpen, path: '/gate-pass', roles: ['parent'], requiresHostel: true },
      
      // ═══════════════════════════════════════════════════════════════
      // TRANSPORT & GPS
      // ═══════════════════════════════════════════════════════════════
      { name: 'Transport', icon: Bus, path: '/transport', roles: ['school_admin', 'staff'] },
      { name: 'My Transport', icon: Bus, path: '/transport', roles: ['student', 'parent'], requiresTransport: true },
      { name: 'GPS Tracking', icon: MapPin, path: '/gps-tracking', roles: ['school_admin'], requiresTransport: false },
      { name: 'GPS Tracking', icon: MapPin, path: '/gps-tracking', roles: ['parent'], requiresTransport: true },
      
      // ═══════════════════════════════════════════════════════════════
      // COMMUNICATION
      // ═══════════════════════════════════════════════════════════════
      { name: 'Announcements', icon: Bell, path: '/announcements', roles: ['school_admin'] },
      { name: 'View Announcements', icon: Bell, path: '/announcements', roles: ['teacher', 'student', 'parent', 'staff'] },
      { name: 'Notices', icon: Bell, path: '/notices', roles: ['school_admin'] },
      { name: 'View Notices', icon: Bell, path: '/notices', roles: ['teacher', 'student', 'parent', 'staff'] },
      { name: 'Notifications', icon: Bell, path: '/notifications', roles: ['school_admin'] },
      { name: 'Communication', icon: MessageCircle, path: '/communication', roles: ['school_admin', 'teacher', 'parent', 'staff'] },
      
      // ═══════════════════════════════════════════════════════════════
      // STUDENT RECORDS & DOCUMENTS
      // ═══════════════════════════════════════════════════════════════
      { name: 'Certificates', icon: Award, path: '/certificates', roles: ['school_admin'] },
      { name: 'ID Cards', icon: CreditCard, path: '/id-cards', roles: ['school_admin'] },
      { name: 'ID Card Builder', icon: CreditCard, path: '/id-card-builder', roles: ['school_admin'] },
      { name: 'Alumni', icon: GraduationCap, path: '/alumni', roles: ['school_admin'] },
      { name: 'Student Behavior', icon: UserCheck, path: '/behavior', roles: ['school_admin', 'teacher', 'staff'] },
      { name: 'Child Behavior', icon: UserCheck, path: '/parent/behavior', roles: ['parent'] },
      
      // ═══════════════════════════════════════════════════════════════
      // REPORTS & ANALYTICS
      // ═══════════════════════════════════════════════════════════════
      { name: 'Reports', icon: FileText, path: '/reports', roles: ['school_admin'] },
      { name: 'Analytics', icon: BarChart3, path: '/analytics', roles: ['school_admin'] },
      
      // ═══════════════════════════════════════════════════════════════
      // SETTINGS & CONFIGURATION
      // ═══════════════════════════════════════════════════════════════
      { name: 'My Subscription', icon: CreditCard, path: '/my-subscription', roles: ['school_admin'] },
      { name: 'School Settings', icon: Building2, path: '/school-settings', roles: ['school_admin'] },
      { name: 'Notification Settings', icon: Settings, path: '/notification-settings', roles: ['school_admin'] },
      { name: 'Integrations', icon: Settings, path: '/integrations', roles: ['school_admin'] },
      { name: 'Mobile Apps', icon: Smartphone, path: '/app-management', roles: ['school_admin'] },
      { name: 'Legal Pages', icon: FileText, path: '/legal-pages', roles: ['school_admin'] },
      { name: 'User Credentials', icon: Users, path: '/user-credentials', roles: ['school_admin'] },
      { name: 'Password Reset', icon: Key, path: '/password-reset', roles: ['super_admin', 'school_admin'] },
      
      // ═══════════════════════════════════════════════════════════════
      // SUPPORT
      // ═══════════════════════════════════════════════════════════════
      { name: 'Support', icon: Ticket, path: '/tickets', roles: ['school_admin'] },
    ];

    return baseItems.filter(item => {
      // First check role-based access
      if (!item.roles.includes(user?.role)) return false;
      
      // Check transport allocation requirement for student/parent
      if (item.requiresTransport && ['student', 'parent'].includes(user?.role)) {
        if (!hasTransportAllocation) return false;
      }
      
      // Check hostel allocation requirement for student/parent
      if (item.requiresHostel && ['student', 'parent'].includes(user?.role)) {
        if (!hasHostelAllocation) return false;
      }
      
      // For staff role, also check module-based permissions
      if (user?.role === 'staff') {
        return staffHasAccess(item.path);
      }
      
      return true;
    });
  };

  const getDashboardPath = () => {
    switch (user?.role) {
      case 'super_admin': return '/super-admin';
      case 'school_admin': return '/admin';
      case 'teacher': return '/teacher';
      case 'student': return '/student';
      case 'parent': return '/parent';
      case 'staff': return '/staff-dashboard';
      default: return '/auth';
    }
  };

  const navItems = getNavigationItems();

  // Subscription banner helper values - only show for school_admin
  const getSubscriptionBannerProps = () => {
    if (!subscriptionStatus || user?.role !== 'school_admin') return null;
    
    const { status_label, days_left, is_expired, is_expiring_soon, is_trial, is_payment_pending, plan_name, message, pending_invoice } = subscriptionStatus;
    
    let bgColor = 'bg-green-500/10 border-green-500/30 text-green-700 dark:text-green-400';
    let IconComponent = CheckCircle;
    
    // Payment pending takes priority
    if (is_payment_pending) {
      bgColor = 'bg-orange-500/10 border-orange-500/30 text-orange-700 dark:text-orange-400';
      IconComponent = CreditCard;
    } else if (is_expired) {
      bgColor = 'bg-red-500/10 border-red-500/30 text-red-700 dark:text-red-400';
      IconComponent = XCircle;
    } else if (is_expiring_soon) {
      bgColor = 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400';
      IconComponent = AlertTriangle;
    } else if (is_trial) {
      bgColor = 'bg-blue-500/10 border-blue-500/30 text-blue-700 dark:text-blue-400';
      IconComponent = Clock;
    }
    
    return { bgColor, IconComponent, status_label, days_left, plan_name, message, is_expired, is_payment_pending, pending_invoice };
  };

  const bannerProps = getSubscriptionBannerProps();

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 py-6 border-b border-border">
            <img 
              src="https://customer-assets.emergentagent.com/job_school-hub-saas/artifacts/mh8ri9d5_CLOUD%20CAMPUS%20SCHOOLS.png" 
              alt="Cloud Campus Schools"
              className="h-8 w-auto"
            />
            <div>
              <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto px-3 py-4 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
            <ul className="space-y-1 pb-4">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      <Icon className="h-5 w-5" strokeWidth={1.5} />
                      <span className="text-sm font-medium">{item.name}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User section */}
          <div className="border-t border-border p-4">
            <div className="flex items-center gap-3 mb-3 px-2">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-sm font-semibold text-primary">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleTheme}
                data-testid="theme-toggle-btn"
                className="flex-1"
              >
                {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                data-testid="logout-btn"
                className="flex-1"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Read-only banner at very top */}
        {isReadOnly && (
          <div className="bg-red-600 text-white py-2 px-4 text-center text-sm font-medium" data-testid="readonly-banner">
            <XCircle className="h-4 w-4 inline mr-2" />
            Your subscription has expired. Please renew to continue. System is in read-only mode.
          </div>
        )}
        
        {/* Top bar */}
        <header className="sticky top-0 z-40 glassmorphism">
          <div className="flex items-center gap-4 px-4 py-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="sidebar-toggle-btn"
              className="lg:hidden"
            >
              {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
            <h1 className="text-2xl font-bold text-foreground">{title}</h1>
            <div className="ml-auto flex items-center gap-3">
              {/* Subscription status for non-super-admin */}
              {bannerProps && (
                <div className="hidden md:block">
                  <div className={`px-4 py-2 border rounded-lg flex items-center gap-3 text-sm ${bannerProps.bgColor}`} data-testid="subscription-banner">
                    <bannerProps.IconComponent className="h-4 w-4 flex-shrink-0" />
                    <div className="flex-1">
                      {bannerProps.is_payment_pending ? (
                        <>
                          <span className="font-medium">Payment Pending</span>
                          <span className="mx-2">•</span>
                          <span>{bannerProps.plan_name || 'Subscription'} not activated</span>
                        </>
                      ) : (
                        <>
                          <span className="font-medium">{bannerProps.plan_name || 'Subscription'}</span>
                          <span className="mx-2">•</span>
                          <span>{bannerProps.status_label}</span>
                          {bannerProps.days_left !== undefined && bannerProps.days_left > 0 && (
                            <>
                              <span className="mx-2">•</span>
                              <span>{bannerProps.days_left} days left</span>
                            </>
                          )}
                        </>
                      )}
                    </div>
                    {bannerProps.is_payment_pending && (
                      <Button size="sm" variant="default" className="text-xs bg-orange-600 hover:bg-orange-700" onClick={() => navigate('/my-subscription')}>
                        Pay Now
                      </Button>
                    )}
                    {bannerProps.is_expired && !bannerProps.is_payment_pending && (
                      <Button size="sm" variant="outline" className="text-xs" onClick={() => navigate('/tickets')}>
                        Contact Support
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="h-9 w-9"
                data-testid="header-theme-toggle"
              >
                {theme === 'light' ? (
                  <Moon className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <Sun className="h-5 w-5 text-amber-500" />
                )}
              </Button>
              <NotificationBell />
            </div>
          </div>
          {/* Mobile subscription banner */}
          {bannerProps && (
            <div className="md:hidden px-4 pb-3">
              <div className={`px-4 py-2 border rounded-lg flex items-center gap-3 text-sm ${bannerProps.bgColor}`} data-testid="subscription-banner-mobile">
                <bannerProps.IconComponent className="h-4 w-4 flex-shrink-0" />
                <div className="flex-1">
                  {bannerProps.is_payment_pending ? (
                    <span className="font-medium">Payment Pending - Not Activated</span>
                  ) : (
                    <>
                      <span className="font-medium">{bannerProps.plan_name || 'Subscription'}</span>
                      <span className="mx-2">•</span>
                      <span>{bannerProps.status_label}</span>
                    </>
                  )}
                </div>
                {bannerProps.is_payment_pending && (
                  <Button size="sm" variant="default" className="text-xs bg-orange-600 hover:bg-orange-700" onClick={() => navigate('/my-subscription')}>
                    Pay
                  </Button>
                )}
              </div>
            </div>
          )}
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          {children}
        </main>

        {/* Footer */}
        <footer className="border-t border-border py-4 px-4 md:px-6 lg:px-8">
          <p className="text-sm text-muted-foreground text-center">
            © 2026 Cloud Campus. Powered by{' '}
            <a 
              href="https://www.ncriptech.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline font-medium"
            >
              ncriptech
            </a>
          </p>
        </footer>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Cloudy AI Chatbot - Only for Admin users */}
      {(user?.role === 'school_admin' || user?.role === 'super_admin') && (
        <CloudyAIChatbot />
      )}

      {/* Idle Timeout Warning Modal */}
      <IdleTimeoutWarning
        isOpen={showWarning}
        remainingTime={remainingTime}
        onStayLoggedIn={stayLoggedIn}
        onLogout={idleLogout}
      />
    </div>
  );
};

export default DashboardLayout;
